import React from "react";
import { Crown, Medal, Award } from "lucide-react";

const TIERS = [
  { color: "#FFD700", alt: "#FF007F", icon: Crown, avatar: "w-9 h-9 sm:w-16 sm:h-16", pad: "py-4", name: "text-sm sm:text-xl", num: "text-xl", bgA: "40" },
  { color: "#C9DCFF", alt: "#00F0FF", icon: Medal, avatar: "w-8 h-8 sm:w-14 sm:h-14", pad: "py-3", name: "text-sm sm:text-lg", num: "text-lg", bgA: "26" },
  { color: "#FF9A4D", alt: "#9D00FF", icon: Award, avatar: "w-7 h-7 sm:w-12 sm:h-12", pad: "py-2.5", name: "text-sm sm:text-base", num: "text-base", bgA: "18" },
];

export function RankArrow({ delta, testId }) {
  if (!delta) return null;
  const up = delta > 0;
  const c = up ? "#39FF14" : "#FF007F";
  return (
    <span data-testid={testId} title={`${up ? "Up" : "Down"} ${Math.abs(delta)} since yesterday`}
          className={`lb-arrow lb-num inline-flex items-center gap-0.5 text-[10px] font-bold leading-none ${up ? "lb-arrow-up" : "lb-arrow-down"}`}
          style={{ color: c, textShadow: `0 0 6px ${c}` }}>
      {up ? "▲" : "▼"}{Math.abs(delta)}
    </span>
  );
}

const span = (c) => ({ gridColumn: `span ${c.span} / span ${c.span}` });

function TopAvatars({ avatars, tier, first }) {
  return (
    <div className="flex -space-x-3 shrink-0">
      {avatars.map((src, i) => (
        <div key={i} className={`relative ${tier.avatar} rounded-xl p-[3px] overflow-hidden`}
             style={{ background: first ? undefined : `linear-gradient(140deg, ${tier.color}, ${tier.alt})`, zIndex: avatars.length - i, boxShadow: `0 0 ${first ? 18 : 10}px ${tier.color}${first ? "AA" : "66"}` }}>
          {first && <div className="absolute -inset-1/2" style={{ background: `conic-gradient(from 0deg, ${tier.color}, ${tier.alt}, ${tier.color}, #FFFFFF, ${tier.color})`, animation: "wrSpin 4s linear infinite" }} />}
          <img src={src} alt="" className="relative w-full h-full rounded-[10px] object-cover bg-[#0A051A]" onError={(e) => { e.currentTarget.src = "/player_default.webp"; }} />
        </div>
      ))}
    </div>
  );
}

function TopRow({ r, i, cols, top, testPrefix, onOpen, isMe }) {
  const tier = TIERS[i];
  const first = i === 0;
  const Icon = tier.icon;
  const nameCol = cols[0];
  return (
    <div className={`lb-row-wrap wr-rise ${i < 2 ? "mb-2" : "mb-3"}`} style={{ animationDelay: `${i * 90}ms` }}>
      <div data-testid={`${testPrefix}-row-${i}`} onClick={onOpen}
           className={`lb-podium lb-podium-${i + 1} group relative rounded-xl p-[2px] cursor-pointer transition-transform duration-200 hover:translate-x-1`}
           style={{ background: `linear-gradient(100deg, ${tier.color}, ${tier.alt}${first ? "" : "88"}, ${tier.color}${first ? "" : "55"})`, "--lb-glow": tier.color }}>
        <div className={`relative grid grid-cols-12 gap-2 items-center rounded-[10px] bg-[#110726] overflow-hidden px-4 ${tier.pad}`}
             style={{ backgroundImage: `linear-gradient(90deg, ${tier.color}${tier.bgA}, transparent 60%)` }}>
          {i < 2 && <span className="wr-tile-shine lb-shine pointer-events-none absolute inset-y-0 -left-1/2 w-1/3" />}
          <div className="col-span-1 relative flex flex-col items-center gap-0.5">
            <Icon size={first ? 24 : 18} strokeWidth={2.6} className={first ? "wr-crown" : ""} style={{ color: tier.color, filter: `drop-shadow(0 0 6px ${tier.color})` }} />
            <span className="lb-num font-bold text-lg leading-none" style={{ color: tier.color, textShadow: `0 0 8px ${tier.color}` }} data-testid={`${testPrefix}-rank-${i + 1}`}>{i + 1}</span>
            <RankArrow delta={r.rank_delta} testId={`${testPrefix}-arrow-${i}`} />
          </div>
          <div style={span(nameCol)} className="relative flex items-center gap-2 sm:gap-3 min-w-0">
            <TopAvatars avatars={top.avatars} tier={tier} first={first} />
            <div className="min-w-0">
              <div className={`font-graffiti ${tier.name} leading-tight truncate group-hover:underline underline-offset-4`}
                   style={{ color: first ? tier.color : "#fff", textShadow: `0 0 12px ${tier.color}99, 2px 2px 0 #000` }}>{top.title}</div>
              {top.subtitle && <div className="text-[10px] font-mono text-[#A89BCC] truncate">{top.subtitle}</div>}
              {isMe && <div className="mt-1"><YouPill testId={`${testPrefix}-you-pill-${i}`} /></div>}
            </div>
          </div>
          {cols.slice(1).map((c) => (
            <div key={c.id} style={{ ...span(c), color: c.color }} className={`relative text-right lb-num font-bold ${tier.num}`}>{c.render(r)}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SortHeader({ c, sort, onSort, testPrefix }) {
  if (!c.sortable) return <div style={span(c)} className={c.align === "left" ? "" : "text-right"}>{c.label}</div>;
  const on = sort === c.id;
  return (
    <button style={span(c)} data-testid={`${testPrefix}-sort-${c.id}`} onClick={() => onSort(c.id)}
            className={`text-right uppercase tracking-widest transition-colors hover:text-white ${on ? "text-[#FFE600]" : "text-[#A89BCC]"}`}>
      {c.label}{on ? " ▾" : ""}
    </button>
  );
}

export function RankTable({ cols, rows, color, testPrefix, onOpen, rowKey, empty, sort, onSort, top, me }) {
  return (
    <div>
      <div className="grid grid-cols-12 gap-2 px-4 py-2.5 mb-2 rounded-lg border font-mono text-[10px] uppercase tracking-widest text-[#A89BCC] bg-[#0A051A]/80" style={{ borderColor: `${color}44` }}>
        <div className="col-span-1">#</div>
        {cols.map((c) => <SortHeader key={c.id} c={c} sort={sort} onSort={onSort} testPrefix={testPrefix} />)}
      </div>
      {rows.length === 0 && <div className="p-8 text-center text-[#A89BCC] font-mono text-sm" data-testid={`${testPrefix}-empty`}>{empty}</div>}
      {rows.slice(0, 3).map((r, i) => <TopRow key={rowKey(r, i)} r={r} i={i} cols={cols} top={top(r)} testPrefix={testPrefix} onOpen={() => onOpen(r)} isMe={me?.rank === i + 1} />)}
      {rows.length > 3 && (
        <div className="rounded-xl overflow-hidden border-2 bg-[#0A051A]/80" style={{ borderColor: `${color}44` }}>
          {rows.slice(3).map((r, j) => me?.rank === j + 4 ? (
            <YouRow key={rowKey(r, j + 3)} r={r} rank={j + 4} cols={cols} testPrefix={testPrefix} onOpen={() => onOpen(r)} inline />
          ) : (
            <div key={rowKey(r, j + 3)} data-testid={`${testPrefix}-row-${j + 3}`} onClick={() => onOpen(r)}
                 className="lb-row grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-[#2D1C52]/50 items-center cursor-pointer group transition-[background-color,transform] duration-150 hover:translate-x-1"
                 style={{ "--lb-glow": color, animationDelay: `${Math.min(j, 15) * 30}ms` }}>
              <div className="col-span-1 flex items-center gap-1.5"><span className="lb-num font-bold text-base text-[#A89BCC] group-hover:text-white">{j + 4}</span><RankArrow delta={r.rank_delta} testId={`${testPrefix}-arrow-${j + 3}`} /></div>
              {cols.map((c) => (
                <div key={c.id} style={{ ...span(c), color: c.color }} className={c.align === "left" ? "font-graffiti truncate text-white group-hover:underline underline-offset-2" : "text-right lb-num font-bold text-base"}>
                  {c.render(r)}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      {me && me.rank > 50 && (
        <div className="mt-3">
          <div className="text-center text-[#A89BCC] font-mono text-xs tracking-[0.5em] mb-1">• • •</div>
          <YouRow r={me.row} rank={me.rank} cols={cols} testPrefix={testPrefix} onOpen={() => onOpen(me.row)} />
        </div>
      )}
    </div>
  );
}

const YOU = "#FFE600";

function YouPill({ testId }) {
  return (
    <span data-testid={testId} className="lb-you-pill inline-block shrink-0 px-2 py-0.5 rounded-full font-heading text-[10px] tracking-widest text-[#0A051A]"
          style={{ background: YOU, boxShadow: `0 0 12px ${YOU}` }}>YOU</span>
  );
}

function YouRow({ r, rank, cols, testPrefix, onOpen, inline }) {
  return (
    <div data-testid={`${testPrefix}-you-row`} onClick={onOpen}
         className={`lb-you-row relative grid grid-cols-12 gap-2 px-4 py-3 items-center cursor-pointer group ${inline ? "border-y-2" : "rounded-xl border-2"}`}
         style={{ borderColor: YOU, background: `linear-gradient(90deg, ${YOU}33, ${YOU}0D 60%, ${YOU}22)` }}>
      <div className="col-span-1 flex items-center gap-1.5"><span className="lb-num font-bold text-lg" style={{ color: YOU, textShadow: `0 0 8px ${YOU}` }} data-testid={`${testPrefix}-you-rank`}>{rank}</span><RankArrow delta={r.rank_delta} testId={`${testPrefix}-you-arrow`} /></div>
      {cols.map((c, k) => (
        <div key={c.id} style={{ ...span(c), color: c.color }} className={c.align === "left" ? "flex items-center gap-2 min-w-0 font-graffiti text-white" : "text-right lb-num font-bold text-base"}>
          {k === 0 ? (<><span className="truncate group-hover:underline underline-offset-2">{c.render(r)}</span><YouPill testId={`${testPrefix}-you-pill`} /></>) : c.render(r)}
        </div>
      ))}
    </div>
  );
}

const SPARKS = Array.from({ length: 34 }, (_, i) => ({
  left: (i * 37) % 100, size: 2 + (i % 4), dur: 9 + (i % 7) * 2, delay: -((i * 1.7) % 16),
  color: ["#FFD700", "#FF007F", "#00F0FF", "#FFE600", "#9D00FF"][i % 5],
}));

export function PrestigeBackdrop() {
  return (
    <div className="pointer-events-none fixed inset-0 -z-0 overflow-hidden" data-testid="lb-prestige-backdrop" aria-hidden="true">
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% -10%, #FFD70022, transparent 55%), radial-gradient(ellipse at 10% 100%, #FF007F18, transparent 50%), radial-gradient(ellipse at 90% 100%, #00F0FF14, transparent 50%)" }} />
      <div className="lb-beam lb-beam-l" />
      <div className="lb-beam lb-beam-r" />
      <div className="lb-floor" />
      {SPARKS.map((p, i) => (
        <span key={i} className="lb-spark" style={{ left: `${p.left}%`, width: p.size, height: p.size, background: p.color, boxShadow: `0 0 ${p.size * 3}px ${p.color}`, animationDuration: `${p.dur}s`, animationDelay: `${p.delay}s` }} />
      ))}
      <div className="absolute inset-0 wr-grain" />
    </div>
  );
}
